import React, { useState, useEffect } from 'react';
import { startOfWeek, format } from 'date-fns';
import { Link } from 'react-router-dom';
import { fetchWeeklyDecisions } from '../services/api';
import DatePicker from '../components/DatePicker';
import DecisionList from '../components/DecisionList';
import CircuitDecisions from '../components/CircuitDecisions';

/**
 * Home page for Defense Nuggets
 */
const HomePage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [weekData, setWeekData] = useState(null);
  
  // Default to current date, adjusted to start of week (Sunday)
  const [selectedWeek, setSelectedWeek] = useState(() => {
    const today = new Date();
    const weekStartDate = startOfWeek(today, { weekStartsOn: 0 }); // 0 = Sunday
    return format(weekStartDate, 'yyyy-MM-dd');
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await fetchWeeklyDecisions(selectedWeek);
        setWeekData(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching weekly decisions:', err);
        setError('Failed to load decisions. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedWeek]);

  const handleWeekChange = (newWeekStart) => {
    setSelectedWeek(newWeekStart);
  };

  if (loading) {
    return <div className="loading">Loading decisions...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  if (!weekData) {
    return <div className="no-data">No data available</div>;
  }

  const { week, relevant_decisions, circuits } = weekData;

  return (
    <div className="home-page">
      <header className="app-header">
        <div className="header-main">
          <h1>Defense Nuggets</h1>
          <Link to="/stats" className="stats-link">View Statistics</Link>
        </div>
        <p className="app-description">
          Collecting recent federal appellate decisions with defense wins and defense-friendly rules.
        </p>
      </header>
      
      <section className="controls">
        <DatePicker weekStart={selectedWeek} onWeekChange={handleWeekChange} />
        
        <div className="week-range">
          Showing decisions from {format(new Date(week.start), 'MMMM d, yyyy')} to{' '}
          {format(new Date(week.end), 'MMMM d, yyyy')}
        </div>
      </section>
      
      <section className="relevant-decisions">
        <h2>Decisions Relevant to Defense Attorneys</h2>
        {relevant_decisions.length > 0 ? (
          <>
            {/* Published decisions - always show this section */}
            <div className="decision-section published-section">
              <h3>Published</h3>
              {relevant_decisions.filter(d => d.decision_type === 'published').length > 0 ? (
                <DecisionList
                  decisions={relevant_decisions.filter(d => d.decision_type === 'published')}
                  showRelevance={true}
                  showAnalysis={true}
                />
              ) : (
                <p className="no-decisions"><i>None found.</i></p>
              )}
            </div>
            
            {/* Unpublished decisions - always show this section */}
            <div className="decision-section unpublished-section">
              <h3>Unpublished</h3>
              {relevant_decisions.filter(d => d.decision_type === 'unpublished').length > 0 ? (
                <DecisionList
                  decisions={relevant_decisions.filter(d => d.decision_type === 'unpublished')}
                  showRelevance={true}
                  showAnalysis={true}
                />
              ) : (
                <p className="no-decisions"><i>None found.</i></p>
              )}
            </div>
            
            {/* Unknown publication status decisions - only show if there are some */}
            {relevant_decisions.filter(d => d.decision_type === 'unknown').length > 0 && (
              <div className="decision-section unknown-section">
                <h3>Unknown Publication Status</h3>
                <DecisionList
                  decisions={relevant_decisions.filter(d => d.decision_type === 'unknown')}
                  showRelevance={true}
                  showAnalysis={true}
                />
              </div>
            )}
          </>
        ) : (
          <p className="no-relevant">
            No decisions with defense relevance found for this week.
          </p>
        )}
      </section>
      
      <section className="all-circuits">
        <h2>Decisions Deemed Not Relevant</h2>
        <div className="circuits-container">
          {circuits.map((circuit) => (
            <CircuitDecisions key={circuit.id} circuit={circuit} />
          ))}
        </div>
      </section>
    </div>
  );
};

export default HomePage;